import React, { useState } from 'react';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import { Mail, Send, User, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    
    try {
      await api.post('/contact/submit', formData);
      
      setStatus('success');
      setTimeout(() => {
        setStatus('idle');
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 5000);
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      setStatus('error');
      setTimeout(() => {
        setStatus('idle');
      }, 5000);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-brand-bg pt-24 px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-violet-600/20 rounded-full mb-4">
              <Mail size={32} className="text-violet-500" />
            </div>
            <h1 className="text-5xl font-outfit font-light tracking-tight text-white mb-3">
              {t('contact.title') || 'Contact Us'}
            </h1>
            <p className="text-zinc-400 text-lg">
              {t('contact.subtitle') || 'Have questions? We\'re here to help.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              <div className="bg-violet-900/20 border border-violet-600/30 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-3">
                  {t('contact.responseTime') || 'Response Time'}
                </h3>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  {t('contact.responseDesc') || 'We typically respond within 24-48 hours during business days.'}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-brand-surface border border-brand-border rounded-2xl p-8">
                {status === 'success' ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send size={32} className="text-green-500" />
                    </div>
                    <h3 className="text-2xl font-outfit font-semibold text-white mb-2">
                      {t('contact.thankYou') || '¡Gracias!'}
                    </h3>
                    <p className="text-zinc-400">
                      {t('contact.messageSent') || 'Tu mensaje ha sido enviado exitosamente. Te responderemos pronto.'}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-2">
                        <User size={16} className="inline mr-2" />
                        {t('contact.name') || 'Nombre'} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={status === 'loading'}
                        className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all disabled:opacity-50"
                        placeholder={t('contact.namePlaceholder') || 'Tu nombre'}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-2">
                        <Mail size={16} className="inline mr-2" />
                        {t('contact.emailLabel') || 'Email'} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={status === 'loading'}
                        className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all disabled:opacity-50"
                        placeholder={t('contact.emailPlaceholder') || 'tu.email@ejemplo.com'}
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-2">
                        <MessageSquare size={16} className="inline mr-2" />
                        {t('contact.subject') || 'Asunto'} *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        disabled={status === 'loading'}
                        className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all disabled:opacity-50"
                        placeholder={t('contact.subjectPlaceholder') || '¿Sobre qué es tu consulta?'}
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-400 mb-2">
                        {t('contact.message') || 'Mensaje'} *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        disabled={status === 'loading'}
                        rows={6}
                        className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-3 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all resize-none disabled:opacity-50"
                        placeholder={t('contact.messagePlaceholder') || 'Cuéntanos más sobre tu consulta...'}
                      />
                    </div>

                    {/* Status Messages */}
                    {status === 'error' && (
                      <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                        <p className="text-red-400 text-sm">
                          ❌ {t('contact.errorMessage') || 'Error al enviar el mensaje. Por favor, intenta de nuevo.'}
                        </p>
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold hover:from-violet-500 hover:to-purple-500 transition-all flex items-center justify-center space-x-2 shadow-lg shadow-violet-600/30 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={20} />
                      <span>
                        {status === 'loading' 
                          ? (t('contact.sending') || 'Enviando...')
                          : (t('contact.send') || 'Enviar Mensaje')
                        }
                      </span>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
